// Sprint 1 — Onboarding post-registro
import { Plus, Trash2 } from "lucide-react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import type { DayRange } from "@/context/onboarding-wizard-context";
import { useOnboardingWizard } from "@/context/onboarding-wizard-context";

const DAY_LABELS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

function toMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function slotCount(start: string, end: string, slotDuration: number): number {
  const span = toMinutes(end) - toMinutes(start);
  if (span <= 0 || slotDuration <= 0) return 0;
  return Math.floor(span / slotDuration);
}

export function OnboardingStepRanges() {
  const { state, dispatch } = useOnboardingWizard();
  const enabledDays = state.days.filter((d) => d.enabled);

  function setRanges(day: number, ranges: DayRange[]) {
    dispatch({ type: "SET_DAY_CONFIG", day, config: { ranges } });
  }

  function addRange(day: number, current: DayRange[]) {
    setRanges(day, [...current, { start: "09:00", end: "17:00" }]);
  }

  function removeRange(day: number, idx: number, current: DayRange[]) {
    if (current.length <= 1) return;
    setRanges(
      day,
      current.filter((_, i) => i !== idx),
    );
  }

  function patchRange(day: number, idx: number, patch: Partial<DayRange>, current: DayRange[]) {
    const next = current.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setRanges(day, next);
  }

  const allValid = enabledDays.every((d) =>
    d.ranges.every((r) => toMinutes(r.start) < toMinutes(r.end)),
  );

  return (
    <div>
      <h1 className={css({ fontSize: "2xl", fontWeight: "bold", color: "#0f172a", mb: "2" })}>
        ¡Buenísimo! Sigamos con los horarios.
      </h1>
      <p className={css({ fontSize: "sm", color: "#64748b", mb: "2", maxW: "42rem", lineHeight: 1.6 })}>
        ¿Cuáles son los rangos horarios de atención de cada día?
      </p>
      <p className={css({ fontSize: "sm", color: "#64748b", mb: "8", maxW: "42rem", lineHeight: 1.6 })}>
        Necesitamos los rangos enteros, no de cada turno. Por ejemplo: de 9:00 a 13:00 y de 15:00 a 19:00.
      </p>

      <div className={css({ display: "flex", flexDir: "column", gap: "6", mb: "8" })}>
        {enabledDays.map((d) => (
          <div
            key={d.day}
            className={cx(
              cf.dashSurface,
              css({ p: "5", display: "flex", flexDir: "column", gap: "4" }),
            )}
          >
            <p className={css({ fontWeight: "semibold", color: "#0f172a", fontSize: "sm" })}>
              {DAY_LABELS[d.day]}
            </p>
            {d.ranges.map((r, idx) => {
              const count = slotCount(r.start, r.end, state.slotDuration);
              return (
                <div
                  key={idx}
                  className={css({
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "3",
                  })}
                >
                  <input
                    type="time"
                    value={r.start}
                    onChange={(e) => patchRange(d.day, idx, { start: e.target.value }, d.ranges)}
                    className={cx(cf.input, css({ w: "auto", minW: "8rem" }))}
                  />
                  <span className={css({ color: "#94a3b8" })}>—</span>
                  <input
                    type="time"
                    value={r.end}
                    onChange={(e) => patchRange(d.day, idx, { end: e.target.value }, d.ranges)}
                    className={cx(cf.input, css({ w: "auto", minW: "8rem" }))}
                  />
                  <span
                    className={css({
                      fontSize: "xs",
                      fontWeight: "semibold",
                      px: "2.5",
                      py: "1",
                      rounded: "md",
                      bg: "#f1f5f9",
                      color: "#475569",
                    })}
                  >
                    {count} horario{count === 1 ? "" : "s"}
                  </span>
                  <button
                    type="button"
                    onClick={() => addRange(d.day, d.ranges)}
                    className={css({
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "1",
                      fontSize: "xs",
                      fontWeight: "semibold",
                      px: "3",
                      py: "2",
                      rounded: "md",
                      borderWidth: "1px",
                      borderColor: "#e2e8f0",
                      bg: "white",
                      cursor: "pointer",
                      color: "#334155",
                    })}
                  >
                    <Plus className={css({ w: "4", h: "4" })} />
                    Franja
                  </button>
                  {d.ranges.length > 1 ? (
                    <button
                      type="button"
                      aria-label="Eliminar franja"
                      onClick={() => removeRange(d.day, idx, d.ranges)}
                      className={css({
                        p: "2",
                        rounded: "md",
                        border: "none",
                        bg: "transparent",
                        color: "#b91c1c",
                        cursor: "pointer",
                        _hover: { bg: "rgba(185,28,28,0.08)" },
                      })}
                    >
                      <Trash2 className={css({ w: "4", h: "4" })} />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {allValid && enabledDays.length > 0 ? (
        <button
          type="button"
          onClick={() => dispatch({ type: "NEXT_STEP" })}
          className={cx(
            cf.linkPrimaryOnDark,
            css({
              px: "6",
              py: "3",
              border: "none",
              fontSize: "sm",
              cursor: "pointer",
              bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
            }),
          )}
        >
          Continuar
        </button>
      ) : null}
    </div>
  );
}
